import 'antd/dist/reset.css';
import styled, { createGlobalStyle } from "styled-components";
import { useLoginMutation, useAskForPasswordResetMutation, useRenewLoginTokenMutation, useLoginExternalChatOnlineMutation } from 'graphql/User/UserMutations'
import Icon, { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Input, Button, notification, Layout, Spin, Form, Divider, Alert } from 'antd';
import FireflyLogo from 'assets/firefly_logo_horizontal.png'
import User, { AuthPayload, UserContext } from 'graphql/User/User';
import { ChangeEvent, useCallback, useContext, useEffect, useState } from 'react';
import MicrosoftLogo from "assets/MicrosoftLogo";
import { ROUTES } from 'pages/Root/PagesController';
import { ExternalPartner } from 'graphql/apollo-graphql-generated/globalTypes';
import React from 'react';
import { load as loadRecaptchaV3, ReCaptchaInstance } from 'recaptcha-v3'
import { reportToBugsnag } from 'utils/reportToBugsnag';
import { LoginExternalChatOnlineMutation_loginExternalChatOnline } from 'graphql/User/apollo-graphql-generated/LoginExternalChatOnlineMutation';
import PageLoadingIndicator from 'components/PageLoadingIndicator';

const ShowRecaptchaBadgeStyle = createGlobalStyle`
  .grecaptcha-badge{ visibility: visible !important }
`

const { Header, Content } = Layout;
// styles
const LoginDiv = styled.div`
  padding: 17px;
  width: 350px;
  text-align: center;
  background-color: white;
`;

interface Props {
  onSuccessfulLogin: (authPayload: Pick<AuthPayload, 'token' | 'user'>, options?: { redirectTo?: string, isExternalLogin?: boolean }) => void
  onSuccessfulLoginExternalChatOnline: (loginResult: LoginExternalChatOnlineMutation_loginExternalChatOnline, options?: { redirectTo?: string }) => void
  isRefreshingUserToken?: boolean
  externalLoginToken?: string
  externalLoginClientPhone?: string
  externalLoginClientName?: string
  externalLoginClientMessage?: string
  externalLoginClientBusinessType?: string
  externalLoginShowMessagesOnly?: string
  clientSecretToShare?: string
  clientIdToShare?: string
}

const LoginFormNoWrap = (props: Props) => {
  const {
    onSuccessfulLogin,
    onSuccessfulLoginExternalChatOnline,
    externalLoginToken,
    externalLoginClientPhone,
    externalLoginClientMessage,
    externalLoginClientBusinessType,
    externalLoginClientName,
    externalLoginShowMessagesOnly,
    clientSecretToShare,
    clientIdToShare,
  } = props

  document.title = "Login - Dashboard " + (process.env.REACT_APP_WHITELABEL_PARTNER_PRETTY || "Firefly");
  const { user } = useContext(UserContext)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCodeRequired, setMfaCodeRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [isTryingLogin, setIsTryingLogin] = useState(!!externalLoginToken);
  const [error, setError] = useState("")
  const [showTokenMissingError, setShowTokenMissingError] = useState(false)
  const [loading, setLoading] = useState(false)
  const recaptchaRef = React.useRef<ReCaptchaInstance>();

  const loadRecaptcha = async () => {
    if (recaptchaRef.current) return

    const res = await loadRecaptchaV3(process.env.REACT_APP_RECAPTCHA_KEY, { useEnterprise: true })
    recaptchaRef.current = res
  }
  useEffect(() => {
    setTimeout(() => {
      if (!externalLoginToken || !isTryingLogin)
        loadRecaptcha()
    }, 100);
  }, [externalLoginToken, isTryingLogin])

  useEffect(() => {
    setTimeout(() => {
      if (process.env.REACT_APP_WHITELABEL_BUILD && !externalLoginToken) {
        setShowTokenMissingError(true)
        // setIsTryingLogin(false)
      }
    }, 5000)
  }, [externalLoginToken])

  // const onSuccessfulLoginExternalChatOnline = (loginResult: LoginExternalChatOnlineMutation_loginExternalChatOnline, options?: { redirectTo?: string }) => {
  //   // TODO: save all vars in local storage
  //   onSuccessfulLogin(loginResult?.authPayload, { redirectTo: options?.redirectTo, isExternalLogin: true })
  // }

  const [loginMutation] = useLoginMutation({
    update: (proxy, mutationResult) => {
      //console.log(mutationResult);
      // setIsTryingLogin(false)
      const params = new URLSearchParams(window.location.search)
      const redirectToArg = params.get('redirectTo') || ''
      const redirectTo = decodeURIComponent(redirectToArg) || undefined

      if (mutationResult.data.login.token)
        onSuccessfulLogin(mutationResult.data.login, { redirectTo })
      else if (mutationResult.data.login.mfaRequired)
        setMfaCodeRequired(true)
      else
        console.log("Login not successful")
    },
  });

  const [loginExternalChatOnlineMutation] = useLoginExternalChatOnlineMutation({
    variables: { token: externalLoginToken, externalPartner: process.env.REACT_APP_WHITELABEL_PARTNER as ExternalPartner },
    update: (proxy, mutationResult) => {
      //console.log(mutationResult);
      // setIsTryingLogin(false)

      if (!mutationResult?.data?.loginExternalChatOnline?.authPayload?.user) {
        notification.error({ message: 'Erro de login', description: 'Usuário inválido. Por favor, entre em contato com o suporte.' })
        return
      }

      const user = new User(mutationResult.data.loginExternalChatOnline.authPayload.user)

      let externalChatChannels = user.getAllExternalChatChannelsIdsWithPermission()
      if (!externalChatChannels.length)
        externalChatChannels = user.getAllExternalChatChannelsIds()

      const externalChatChannelIds =
        mutationResult?.data?.loginExternalChatOnline?.selectedExternalChatChannelIds?.length ?
          mutationResult?.data?.loginExternalChatOnline?.selectedExternalChatChannelIds
          : externalChatChannels.join(',');

      const redirectToUrl = ROUTES.chatOnlineExternalChatChannel.getUrl(externalChatChannelIds)

      const clientPhone = externalLoginClientPhone?.replace(/[^0-9]/g, '')
      const clientName = externalLoginClientName
      const clientMessage = externalLoginClientMessage
      const clientBusinessType = externalLoginClientBusinessType

      let redirectToParams =
        (clientPhone ? `?clientPhone=${encodeURIComponent(clientPhone)}` : '') +
        (clientName ? `&clientName=${encodeURIComponent(clientName)}` : '') +
        (clientMessage ? `&clientMessage=${encodeURIComponent(clientMessage)}` : '') +
        (clientBusinessType ? `&clientBusinessType=${encodeURIComponent(clientBusinessType)}` : '') +
        (externalLoginShowMessagesOnly ? `&showMessagesOnly=true` : '');

      redirectToParams = '?' + redirectToParams.substring(1)

      const redirectTo = redirectToUrl + redirectToParams


      if (mutationResult.data.loginExternalChatOnline)
        onSuccessfulLoginExternalChatOnline(mutationResult.data.loginExternalChatOnline, { redirectTo })
      else
        console.log("Login not successful")
    },
  });

  const [renewTokenMutation] = useRenewLoginTokenMutation();

  /** used to fetch complete user from the login token string */
  const renewToken = async (token: string) => {
    try {
      const res = await renewTokenMutation({ variables: { token } })

      if (res.data) {
        // remove all the search params from the url
        window.history.replaceState(null, null, window.location.pathname)
        onSuccessfulLogin(res.data?.renewLoginToken)
      }

    } catch (e: any) {
      notification.error({ message: 'Erro de login', description: String(e) })
    }
  }

  useEffect(() => {
    // if authToken is specified in url params, fetch complete user info and save it in local storage
    const search = window.location.search; // could be '?foo=bar'
    const params = new URLSearchParams(search);
    const authToken = params.get('authToken')

    if (authToken)
      renewToken(authToken)
    // eslint-disable-next-line
  }, [])

  const openLoginErrorNotification = (message: string, description: string) => {
    notification.open({
      message,
      description,
    });
  };

  const doLogin = useCallback(async (isExternalLogin?: boolean) => {
    if (!recaptchaRef.current && !isExternalLogin) {
      openLoginErrorNotification('Erro', 'Script captcha não carregado. Aguarde alguns segundos e tente novamente.')
    }

    setIsTryingLogin(true)

    const recaptchaToken = !isExternalLogin ? await recaptchaRef?.current?.execute('LOGIN') : null
    // console.log({ recaptchaToken })

    try {
      if (isExternalLogin)
        await loginExternalChatOnlineMutation()
      else
        await loginMutation({ variables: { email, password, recaptchaToken, mfaCode, clientSecretToShare, clientIdToShare } })

    } catch (e: any) {
      reportToBugsnag({ error: e, user, caller: 'doLogin' });
      if (Array.isArray(e.graphQLErrors) && e.graphQLErrors[0] && e.graphQLErrors[0].extensions) {
        if (e.graphQLErrors[0].extensions.code === "LoginInvalidError")
          openLoginErrorNotification('Erro no login', (e.graphQLErrors[0].message || ''))
        else if (e.graphQLErrors[0].extensions.code === "PasswordResetRequiredError")
          notification.info({ message: 'Resetar senha', description: e.graphQLErrors[0].message || '' })
        else if (e.graphQLErrors[0].extensions.code)
          notification.open({ message: 'Erro', description: e.graphQLErrors[0].message || '' })

        setError(e.graphQLErrors[0].message || '')

        await new Promise(resolve => setTimeout(resolve, 4000))
      } else {
        openLoginErrorNotification('Problema técnico', 'Ocorreu um problema técnico. Por favor tente novamente mais tarde. ' + e.message)
      }
      // console.log(JSON.stringify(e))
      // console.log(e.graphQLErrors[0])
    } finally {
      setTimeout(() => {
        setIsTryingLogin(false);
      }, 1000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginExternalChatOnlineMutation, loginMutation, user, email, password, mfaCode])

  useEffect(() => {
    if (externalLoginToken)
      doLogin(true)
  }, [doLogin, externalLoginToken])

  const [resetPasswordMutation] = useAskForPasswordResetMutation({
    variables: { email },
    update: (proxy, mutationResult) => {
      //console.log(mutationResult);

      if (mutationResult.data.askForPasswordReset) {
        openLoginErrorNotification('Redefinição de senha', 'O link para redefinição de senha foi enviado para ' + email)
        //props.onSuccessfulLogin(mutationResult.data.changePassword)
      } else
        console.log("Password change not successful")
    },
  });

  const doForgotPassword = async () => {
    setLoading(true)

    if (!email) {
      openLoginErrorNotification('Erro para redefinição de senha', 'Campo de usuário não preenchido')
      setLoading(false)
      return
    }

    try {
      await resetPasswordMutation()
      //openLoginErrorNotification('Redefinição de senha', 'O link para redefinição de senha foi enviado para '+ email)

    } catch (e: any) {
      reportToBugsnag({ error: e, user, caller: 'doForgotPassword' });
      if (Array.isArray(e.graphQLErrors) && e.graphQLErrors[0] && e.graphQLErrors[0].extensions && e.graphQLErrors[0].extensions.code === 'LoginInvalidError')
        openLoginErrorNotification('Erro para redefinição de senha', 'Não existe um usuário com esse login/e-mail')
      else
        openLoginErrorNotification('Problema técnico', 'Ocorreu um problema técnico. Por favor tente novamente mais tarde. ' + e.message)

    } finally {
      setLoading(false)
    }
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newEmail = e.target.value.trim()
    // props.form.setFieldsValue({ email: newEmail });
    setEmail(newEmail)
  }
  const [form] = Form.useForm();

  const renderWhitelabelPage = () => {
    if (error)
      return <div style={{ width: '100vw', height: '100vh' }}>
        <Alert type='error' message={error} />
      </div>

    if (!externalLoginToken && showTokenMissingError)
      return <div style={{ width: '100vw', height: '100vh' }}>
        <Alert type='error' message='Token faltando na requisição' />
      </div>

    // if (isTryingLogin)
    return <div style={{ width: '100vw', height: '100vh' }}>
      <PageLoadingIndicator debug='LoginForm' />
    </div>
  }

  return (
    process.env.REACT_APP_WHITELABEL_BUILD ? renderWhitelabelPage()
      :
      <Layout style={{ height: '100vh' }}>
        <Header>
          {!process.env.REACT_APP_WHITELABEL_BUILD &&
            <img src={FireflyLogo} alt="FireFly logo" style={{ height: '42px' }} />}
        </Header>
        <Content style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ display: 'inline-block' }}>
            <LoginDiv>
              <Spin spinning={props.isRefreshingUserToken || loading}>
                <h2>Login</h2>
                <Form form={form} name='LoginForm' onFinish={(e) => { doLogin() }} className="login-form">
                  <Form.Item
                    name='email'
                    rules={[
                      { required: true, message: 'Por favor inserir o email' },
                      { type: 'email', message: 'Email inválido' },
                    ]}
                    normalize={(value) => value.trim().toLowerCase()}
                  >
                    <Input
                      prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                      placeholder="Usuário"
                      onChange={handleEmailChange}
                      disabled={mfaCodeRequired}
                    />
                  </Form.Item>

                  <Form.Item name='password' rules={[
                    { required: true, message: 'Por favor inserir a senha' }
                  ]}>
                    <Input
                      prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                      type="password"
                      placeholder="Senha"
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={mfaCodeRequired}
                    />
                  </Form.Item>

                  {mfaCodeRequired &&
                    <Alert
                      type='warning'
                      // showIcon
                      message='Informe abaixo o código de autenticação enviado ao seu email:'
                      style={{ marginBottom: '0.5em' }}
                    />}

                  {mfaCodeRequired &&
                    <Form.Item name='mfaCode' rules={[
                      { required: true, message: 'Por favor inserir o código' }
                    ]}>
                      <Input
                        prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                        type="text"
                        placeholder="Código de autenticação"
                        onChange={(e) => setMfaCode(e.target.value)}
                      />
                    </Form.Item>}

                  <Form.Item>
                    <Button type="primary" size='large' htmlType="submit" className="login-form-button" disabled={isTryingLogin} loading={isTryingLogin}>
                      Entrar
                    </Button>

                    {!process.env.REACT_APP_WHITELABEL_BUILD &&
                      <div
                        style={{ color: "blue", cursor: "pointer", marginTop: '20px' }}
                        onClick={doForgotPassword}
                      >
                        Esqueci minha senha
                      </div>}
                  </Form.Item>
                </Form>
              </Spin>

              <Divider />

              {!process.env.REACT_APP_WHITELABEL_BUILD &&
                <Button
                  size='large'
                  icon={<Icon component={MicrosoftLogo} />}
                  href={process.env.REACT_APP_MICROSOFT_LOGIN_PAGE}
                >
                  Login com microsoft
                </Button>}

              {/* {!process.env.REACT_APP_WHITELABEL_BUILD &&
                <Button
                  size='large'
                  icon={<FacebookOutlined style={{ color: "#027AEE", marginRight: "5px" }} />}
                  href={process.env.REACT_APP_FACEBOOK_LOGIN_PAGE}
                  style={{ marginTop: '10px' }}
                >
                  Login com facebook
                </Button>} */}

              {/* {!process.env.REACT_APP_WHITELABEL_BUILD &&
                <Button
                  size='large'
                  icon={<InstagramOutlined style={{ color: "#241d09", marginRight: "5px" }} />}
                  href={process.env.REACT_APP_INSTAGRAM_LOGIN_PAGE}
                  style={{ marginTop: '10px' }}
                >
                  Login com instagram
                </Button>} */}

              {/* <Button icon={<ReloadOutlined />}>Login com microsoft</Button> */}
            </LoginDiv>
          </div>
        </Content>
        {/* <Footer style={{ textAlign: 'center' }}>Este site é protegido por reCAPTCHA e as políticas do Google se aplicam:
          <a href="https://policies.google.com/privacy"> Política de Privacidade</a> e
          <a href="https://policies.google.com/terms"> Termos de serviço</a>.
        </Footer> */}

        {!externalLoginToken &&
          <ShowRecaptchaBadgeStyle />}

      </Layout>
  );
}

const LoginForm = LoginFormNoWrap

export default LoginForm;
